/**
 * Hook de métricas de performance do chat
 * Lê e gerencia snapshots via RPCs SECURITY DEFINER (root admin only)
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { showToast } from '@/lib/toast';
import { logger } from '@/lib/logger';

// Chaves canônicas das queries monitoradas
export const CHAT_PERF_KEYS = [
  'msgs_select_direct',
  'msg_attachments_select_direct',
  'rpc_get_conversation_messages_page',
  'rpc_get_message_full_by_id',
  'rpc_resolve_reply_to_id_by_external',
  'rpc_count_conversation_messages',
] as const;

export type ChatPerfKey = (typeof CHAT_PERF_KEYS)[number];

export interface ChatPerfMetricRow {
  query_key: ChatPerfKey;
  calls: number;
  total_ms: number;
  mean_ms: number;
  rows: number;
  blks_hit: number;
  blks_read: number;
}

export interface ChatPerfTableRow {
  relname: string;
  seq_scan: number;
  seq_tup_read: number;
  idx_scan: number;
  idx_tup_fetch: number;
  n_tup_ins: number;
  n_tup_upd: number;
  n_tup_del: number;
  n_live_tup: number;
  n_dead_tup: number;
}

export interface ChatPerfSnapshotSeries {
  id: string;
  captured_at: string;
  label: string;
  metrics: ChatPerfMetricRow[];
}

export interface ChatPerfPayload {
  current: ChatPerfMetricRow[];
  tables: ChatPerfTableRow[];
  baseline: ChatPerfSnapshotSeries | null;
  series: ChatPerfSnapshotSeries[];
  captured_at: string;
}

export function useChatPerfMetrics(autoRefresh = true) {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ['chat-perf-metrics'],
    queryFn: async (): Promise<ChatPerfPayload> => {
      const { data, error } = await supabase.rpc('get_chat_perf_metrics' as any);
      if (error) {
        logger.error('[useChatPerfMetrics] erro', error.message);
        throw error;
      }
      return data as ChatPerfPayload;
    },
    refetchInterval: autoRefresh ? 30_000 : false,
    staleTime: 10_000,
  });

  const captureSnapshot = useMutation({
    mutationFn: async (label: string) => {
      const { data, error } = await supabase.rpc(
        'capture_chat_perf_snapshot' as any,
        { p_label: label }
      );
      if (error) throw error;
      return data as string;
    },
    onSuccess: () => {
      showToast.success('Snapshot capturado');
      qc.invalidateQueries({ queryKey: ['chat-perf-metrics'] });
    },
    onError: (err: any) => showToast.error(err.message || 'Falha ao capturar snapshot'),
  });

  const resetBaseline = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.rpc('reset_chat_perf_baseline' as any);
      if (error) throw error;
      return data as string;
    },
    onSuccess: () => {
      showToast.success('Baseline redefinido');
      qc.invalidateQueries({ queryKey: ['chat-perf-metrics'] });
    },
    onError: (err: any) => showToast.error(err.message || 'Falha ao redefinir baseline'),
  });

  const deleteSnapshot = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.rpc('delete_chat_perf_snapshot' as any, { p_id: id });
      if (error) throw error;
    },
    onSuccess: () => {
      showToast.success('Snapshot removido');
      qc.invalidateQueries({ queryKey: ['chat-perf-metrics'] });
    },
    onError: (err: any) => showToast.error(err.message || 'Falha ao remover snapshot'),
  });

  return { ...query, captureSnapshot, resetBaseline, deleteSnapshot };
}
